import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { openSnackbarMain } from 'components/Shared/openSnackbarMain';
import {
  getFiltroBancos,
  getFiltroEmpresas,
  getFiltroCarteiras,
  getFiltroOcorrenciasBancarias,
  getFiltroTiposDocumento
} from '../../../../api/remessas';

const getDataIncluidoDePadrao = () => {
  const dataPadrao = new Date();
  dataPadrao.setDate(dataPadrao.getDate() - 30);
  const ano = dataPadrao.getFullYear();
  const mes = String(dataPadrao.getMonth() + 1).padStart(2, '0');
  const dia = String(dataPadrao.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
};

const buildValoresIniciais = ({ incluirPadraoIncluidoDe = false } = {}) => ({
  codigoRemessaDe: '',
  codigoRemessaAte: '',
  remessaBancoDe: '',
  remessaBancoAte: '',
  operacao: '',
  banco: '',
  codigoTitulo: '',
  nomeSacado: '',
  nomeCedente: '',
  cnpjSacado: '',
  cnpjCedente: '',
  vencimentoDe: '',
  vencimentoAte: '',
  entradaDe: '',
  entradaAte: '',
  incluidoDe: incluirPadraoIncluidoDe ? getDataIncluidoDePadrao() : '',
  incluidoAte: '',
  valorDe: '',
  valorAte: '',
  documento: '',
  notaFiscal: '',
  tipoDocumento: '',
  empresa: '',
  carteira: '',
  ocorrenciaBancaria: '',
  remessasBaixaCustodia: false
});

const normalizarOpcoesBancos = opcoes =>
  (opcoes ?? [])
    .map(opcao => {
      const codigo =
        opcao?.codigo ?? opcao?.cod_banco ?? opcao?.codBanco ?? opcao?.id ?? opcao?.value ?? '';
      const label =
        opcao?.label ?? opcao?.nome_banco ?? opcao?.nomeBanco ?? opcao?.descricao ?? opcao?.nome ?? '';

      if (codigo === '' || label === '') return null;

      return {
        ...opcao,
        codigo,
        label
      };
    })
    .filter(Boolean);

const useDrawerFiltrosHook = ({ onAplicarFiltros, onLimparFiltros, filtrosAplicados }) => {
  const formikRef = useRef(null);
  const [empresas, setEmpresas] = useState([]);
  const [carteiras, setCarteiras] = useState([]);
  const [ocorrenciasBancarias, setOcorrenciasBancarias] = useState([]);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [carregandoFiltros, setCarregandoFiltros] = useState(false);

  useEffect(() => {
    setCarregandoFiltros(true);
    Promise.all([
      getFiltroBancos(),
      getFiltroEmpresas(),
      getFiltroCarteiras(),
      getFiltroOcorrenciasBancarias(),
      getFiltroTiposDocumento()
    ])
      .then(([resBancos, resEmpresas, resCarteiras, resOcorrencias, resTipos]) => {
        const dadosBancos = resBancos?.data?.data ?? resBancos?.data ?? [];
        setBancos(normalizarOpcoesBancos(dadosBancos));
        setEmpresas(resEmpresas.data.data ?? []);
        setCarteiras(resCarteiras.data.data ?? []);
        setOcorrenciasBancarias(resOcorrencias.data.data ?? []);
        setTiposDocumento(resTipos.data.data ?? []);
      })
      .catch(() => {
        openSnackbarMain('Ocorreu um erro ao carregar os filtros', 'error');
      })
      .finally(() => setCarregandoFiltros(false));
  }, []);

  const existemFiltrosAplicados = Boolean(filtrosAplicados && Object.keys(filtrosAplicados).length > 0);

  const valoresIniciais = useMemo(
    () => ({
      ...buildValoresIniciais({ incluirPadraoIncluidoDe: !existemFiltrosAplicados }),
      ...(filtrosAplicados ?? {})
    }),
    [filtrosAplicados, existemFiltrosAplicados]
  );

  const handleSubmit = useCallback(
    values => {
      const filtrosPreenchidos = {};
      Object.entries(values).forEach(([chave, valor]) => {
        if (chave === 'remessasBaixaCustodia') {
          if (valor === true) filtrosPreenchidos[chave] = true;
        } else if (valor !== '' && valor !== null && valor !== undefined) {
          filtrosPreenchidos[chave] = valor;
        }
      });
      onAplicarFiltros(filtrosPreenchidos);
    },
    [onAplicarFiltros]
  );

  const handleLimpar = useCallback(() => {
    if (formikRef.current) {
      formikRef.current.resetForm({
        values: buildValoresIniciais({ incluirPadraoIncluidoDe: true })
      });
    }
    onLimparFiltros();
  }, [onLimparFiltros]);

  return {
    formikRef,
    valoresIniciais,
    handleSubmit,
    handleLimpar,
    empresas,
    carteiras,
    ocorrenciasBancarias,
    tiposDocumento,
    bancos,
    carregandoFiltros
  };
};

export default useDrawerFiltrosHook;
